import {
  ProgressInfo,
  UpdateDownloadedEvent,
  UpdateInfo,
} from 'electron-updater'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { observe } from '~/services/observability/observe'
import { AnalyticsNamespace, DesktopAnalyticsEvents } from '~/types/Analytics'
import { isElectron } from '~/utils/common'
import { classifyUpdateError } from '~/utils/updateErrorReason'

import type { UpdateOutcome, UpdateRequirement } from '@mangovoice/types'

/** Outcome of the most recent update check; `pending` until one finishes. */
export type UpdateCheckStatus = 'pending' | 'succeeded' | 'failed'

export interface ElectronUpdaterHookResult {
  availableVersion: string | null
  statusMessage: string
  isUpdateAvailable: boolean
  isUpdateDownloaded: boolean
  isChecking: boolean
  error: string | null
  // Self-update persistently can't complete — a blocked staged copy (AV/EDR) or
  // an unreachable update endpoint (network/DNS/content filter). Not fixable
  // in-app, so the UI shows a persistent notice + reinstall link.
  isUpdateBlocked: boolean
  // Per-brand/channel/OS installer URL (from main); null hides the button.
  updateBlockedDownloadUrl: string | null
  // Last check found nothing newer than the running build.
  isUpdateNotAvailable: boolean
  downloadPercent: number | null
  // Null until main answers, and always null outside Electron.
  updateRequirement: UpdateRequirement | null
  // Result of an install attempted on the previous run; null if none.
  lastInstallOutcome: UpdateOutcome | null
  // The user can't write to the installed app, so updates would need an admin
  // password. Holds the help-article URL; null when updates can install.
  updatePermissionHelpUrl: string | null
  // Unlike `error`, this persists until the next check settles.
  lastCheckStatus: UpdateCheckStatus
  // Goes up each time a check answers that install permission is missing. That
  // answer can repeat with no state change, unlike every other check result.
  // The startup permission pull doesn't count, since no check ran.
  permissionRecheckCount: number
  checkForUpdates: () => void
  /** Installs despite missing permission; macOS will ask for an admin password. */
  updateIgnoringInstallPermission: () => void
  quitAndInstall: () => void
  setClearMessageDelay: (delay: number) => void
}

const trackDesktop = (
  event: DesktopAnalyticsEvents,
  data?: Record<string, unknown>,
) => observe.track(AnalyticsNamespace.DESKTOP, event, data)

export const useElectronUpdater = (
  checkIntervalMs?: number,
  clearMessageDelayMs?: number,
): ElectronUpdaterHookResult => {
  const { t } = useTranslation()
  const [statusMessage, setStatusMessage] = useState<string>('')
  const [isUpdateAvailable, setIsUpdateAvailable] = useState<boolean>(false)
  const [isUpdateDownloaded, setIsUpdateDownloaded] = useState<boolean>(false)
  const [isChecking, setIsChecking] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [isUpdateBlocked, setIsUpdateBlocked] = useState<boolean>(false)
  const [updateBlockedDownloadUrl, setUpdateBlockedDownloadUrl] = useState<
    string | null
  >(null)
  const [updatePermissionHelpUrl, setUpdatePermissionHelpUrl] = useState<
    string | null
  >(null)
  const [availableVersion, setAvailableVersion] = useState<string | null>(null)
  const [isUpdateNotAvailable, setIsUpdateNotAvailable] =
    useState<boolean>(false)
  const [downloadPercent, setDownloadPercent] = useState<number | null>(null)
  const [updateRequirement, setUpdateRequirement] =
    useState<UpdateRequirement | null>(null)
  const [lastInstallOutcome, setLastInstallOutcome] =
    useState<UpdateOutcome | null>(null)
  const [lastCheckStatus, setLastCheckStatus] =
    useState<UpdateCheckStatus>('pending')
  const [permissionRecheckCount, setPermissionRecheckCount] = useState(0)
  const [currentClearDelay, setCurrentClearDelay] = useState<
    number | undefined
  >(clearMessageDelayMs)

  const messageTimerRef = useRef<NodeJS.Timeout | null>(null)
  const intervalTimerRef = useRef<NodeJS.Timeout | null>(null)
  // The update check re-runs every three hours, so an unguarded event would
  // count the same pending update once per check and flatten the funnel.
  const trackedStepsRef = useRef(new Set<DesktopAnalyticsEvents>())

  const trackUpdateStepOnce = useCallback(
    (event: DesktopAnalyticsEvents, data?: Record<string, unknown>) => {
      if (trackedStepsRef.current.has(event)) {
        return
      }
      trackedStepsRef.current.add(event)
      trackDesktop(event, data)
    },
    [],
  )

  const clearMessageTimer = () => {
    if (messageTimerRef.current) {
      clearTimeout(messageTimerRef.current)
      messageTimerRef.current = null
    }
  }

  const resetStatusAfterDelay = (delay?: number) => {
    clearMessageTimer()
    const finalDelay = delay ?? currentClearDelay ?? 8000
    messageTimerRef.current = setTimeout(() => {
      if (!isUpdateDownloaded && !isChecking) {
        setStatusMessage('')
        setError(null)
      }
    }, finalDelay)
  }

  const setClearMessageDelay = useCallback((delay: number) => {
    setCurrentClearDelay(delay)
  }, [])

  const checkForUpdatesRef = useRef<() => void>(() => {})

  const checkForUpdates = useCallback(() => {
    if (isElectron && !isChecking) {
      clearMessageTimer()
      setError(null)
      setStatusMessage('')
      setIsUpdateAvailable(false)
      setIsUpdateDownloaded(false)
      window.electron?.updates.check()
    }
  }, [isElectron, isChecking])

  // Keep ref updated with latest function
  checkForUpdatesRef.current = checkForUpdates

  const updateIgnoringInstallPermission = useCallback(() => {
    if (isElectron && !isChecking) {
      clearMessageTimer()
      setError(null)
      trackDesktop(DesktopAnalyticsEvents.UpdatePermissionBypassed)
      window.electron?.updates.checkIgnoringInstallPermission?.()
    }
  }, [isElectron, isChecking])

  const quitAndInstall = useCallback(() => {
    if (isElectron && isUpdateDownloaded) {
      window.electron?.updates.quitAndInstall()
    }
  }, [isElectron, isUpdateDownloaded])

  // Listener setup
  useEffect(() => {
    if (!isElectron) {
      return
    }

    const handleUpdateChecking = () => {
      clearMessageTimer()
      setError(null)
      setUpdatePermissionHelpUrl(null)
      setStatusMessage(t('updateCheck.checking'))
      setIsChecking(true)
      setIsUpdateNotAvailable(false)
      setIsUpdateAvailable(false)
      setIsUpdateDownloaded(false)
      setAvailableVersion(null)
    }

    const handleUpdateAvailable = (info: UpdateInfo) => {
      clearMessageTimer()
      setError(null)
      setStatusMessage(
        t('updateCheck.versionAvailable', { version: info.version }),
      )
      setIsChecking(true)
      setIsUpdateNotAvailable(false)
      setIsUpdateAvailable(true)
      setIsUpdateDownloaded(false)
      setAvailableVersion(info.version)
      setLastCheckStatus('succeeded')
      trackUpdateStepOnce(DesktopAnalyticsEvents.UpdateOffered, {
        to_version: info.version,
      })
    }

    const handleUpdateNotAvailable = (_info: UpdateInfo) => {
      setError(null)
      // A clean check means whatever was blocking has cleared (or there's
      // nothing to apply) — drop the persistent blocked notice.
      setIsUpdateBlocked(false)
      setStatusMessage(t('updateCheck.upToDate'))
      setIsChecking(false)
      setIsUpdateNotAvailable(true)
      setDownloadPercent(null)
      setIsUpdateAvailable(false)
      setIsUpdateDownloaded(false)
      setAvailableVersion(null)
      setLastCheckStatus('succeeded')
      resetStatusAfterDelay()
    }

    const handleUpdateError = (err: Error, message?: string) => {
      console.error('Electron Update Error:', message || err.message, err)
      const errorMessage = message || err.message || 'Unknown update error'
      setError(errorMessage)
      setLastCheckStatus('failed')
      setStatusMessage(t('updateCheck.checkFailed'))
      setIsChecking(false)
      setDownloadPercent(null)
      setIsUpdateAvailable(false)
      setIsUpdateDownloaded(false)
      setAvailableVersion(null)
      trackUpdateStepOnce(DesktopAnalyticsEvents.UpdateFailed, {
        reason: classifyUpdateError(errorMessage),
      })
      resetStatusAfterDelay()
    }

    // Distinct from a transient error: self-update is persistently blocked —
    // either a blocked staged copy (AV/EDR) or an unreachable update endpoint
    // (network/DNS/content filter, past the failure threshold). Persist this
    // (don't auto-clear) so the escape-hatch banner stays until an update succeeds.
    const handleUpdateBlocked = (
      err: Error,
      message?: string,
      downloadUrl?: string | null,
    ) => {
      console.error('Electron Update Blocked:', message || err.message, err)
      clearMessageTimer()
      setIsUpdateBlocked(true)
      setUpdateBlockedDownloadUrl(downloadUrl ?? null)
      setIsChecking(false)
      setDownloadPercent(null)
      // Clear the download state the failed apply left behind, so the status
      // line and the restart-to-update banner don't contradict the notice.
      setStatusMessage('')
      setIsUpdateAvailable(false)
      setIsUpdateDownloaded(false)
      setAvailableVersion(null)
      trackUpdateStepOnce(DesktopAnalyticsEvents.UpdateBlocked, {
        reason: classifyUpdateError(message || err.message),
      })
    }

    const applyMissingInstallPermission = (helpUrl: string) => {
      clearMessageTimer()
      setUpdatePermissionHelpUrl(helpUrl)
      setIsChecking(false)
      setStatusMessage('')
      trackUpdateStepOnce(DesktopAnalyticsEvents.UpdatePermissionRequired)
    }

    // A check's answer, unlike the status pulled below when the listeners attach.
    const handleUpdatePermissionRequired = (helpUrl: string) => {
      applyMissingInstallPermission(helpUrl)
      setPermissionRecheckCount((count) => count + 1)
    }

    const handleDownloadProgress = (progress: ProgressInfo) => {
      clearMessageTimer()
      setError(null)
      setStatusMessage(
        t('updateCheck.downloading', { percent: progress.percent.toFixed(0) }),
      )
      setDownloadPercent(progress.percent)
      setIsChecking(true)
      setIsUpdateNotAvailable(false)
      setIsUpdateAvailable(true)
      setIsUpdateDownloaded(false)
    }

    const handleUpdateDownloaded = (event: UpdateDownloadedEvent) => {
      clearMessageTimer()
      setError(null)
      // Download succeeded and staged cleanly — clear any prior blocked notice.
      setIsUpdateBlocked(false)
      setStatusMessage(t('updateCheck.downloaded', { version: event.version }))
      setIsChecking(false)
      setIsUpdateNotAvailable(false)
      setDownloadPercent(100)
      setIsUpdateAvailable(true)
      setIsUpdateDownloaded(true)
      setAvailableVersion(event.version)
      setLastCheckStatus('succeeded')
      trackUpdateStepOnce(DesktopAnalyticsEvents.UpdateDownloaded, {
        to_version: event.version,
      })
    }

    window.electron?.receive('update-checking', handleUpdateChecking)
    window.electron?.receive('update-available', handleUpdateAvailable)
    window.electron?.receive('update-not-available', handleUpdateNotAvailable)
    window.electron?.receive('update-error', handleUpdateError)
    window.electron?.receive('update-blocked', handleUpdateBlocked)
    window.electron?.receive(
      'update-permission-required',
      handleUpdatePermissionRequired,
    )
    window.electron?.receive('update-download-progress', handleDownloadProgress)
    window.electron?.receive('update-downloaded', handleUpdateDownloaded)

    window.electron?.updates
      ?.getInstallPermission?.()
      .then((status) => {
        if (status?.missing) {
          applyMissingInstallPermission(status.helpUrl)
        }
      })
      .catch(() => {
        // Older main processes have no handler; the push event still works.
      })

    return () => {
      window.electron?.removeAllListeners('update-checking')
      window.electron?.removeAllListeners('update-available')
      window.electron?.removeAllListeners('update-not-available')
      window.electron?.removeAllListeners('update-error')
      window.electron?.removeAllListeners('update-blocked')
      window.electron?.removeAllListeners('update-permission-required')
      window.electron?.removeAllListeners('update-download-progress')
      window.electron?.removeAllListeners('update-downloaded')
      clearMessageTimer()
    }
  }, [isElectron, t, currentClearDelay, trackUpdateStepOnce])

  // Pulled once because main settles the startup result before this window
  // mounts, then kept current by pushes after each periodic check.
  useEffect(() => {
    if (!isElectron) {
      return
    }
    // The pull and the pushes are unordered: keep whichever main committed last.
    const applyRequirement = (incoming: UpdateRequirement | null) => {
      if (!incoming) {
        return
      }
      setUpdateRequirement((current) =>
        current && incoming.revision < current.revision ? current : incoming,
      )
    }
    window.electron?.receive('update-requirement', applyRequirement)
    window.electron?.updates
      ?.getRequirement?.()
      .then(applyRequirement)
      .catch(() => {
        // Main processes without the handler never enforce a requirement.
      })
    return () => {
      window.electron?.removeAllListeners('update-requirement')
    }
  }, [isElectron])

  // Confirm the previous run's install. Pulled from main rather than pushed —
  // the outcome is settled before this window exists.
  useEffect(() => {
    if (!isElectron) {
      return
    }
    window.electron?.updates
      ?.getLastOutcome?.()
      .then((outcome) => {
        if (!outcome) {
          return
        }
        setLastInstallOutcome(outcome)
        trackDesktop(
          outcome.status === 'applied'
            ? DesktopAnalyticsEvents.UpdateApplied
            : DesktopAnalyticsEvents.UpdateNotApplied,
          {
            from_version: outcome.fromVersion,
            to_version: outcome.toVersion,
            running_version: outcome.runningVersion,
            quit_reason: outcome.quitReason ?? null,
            elevation_required: outcome.elevationRequired ?? null,
            user_is_admin: outcome.userIsAdmin ?? null,
            installer_result: outcome.installerResult ?? null,
          },
        )
      })
      .catch(() => {
        // Older main processes have no handler; the funnel degrades to the
        // download step rather than failing the hook.
      })
  }, [isElectron])

  // Interval check
  useEffect(() => {
    const clearExistingInterval = () => {
      if (intervalTimerRef.current) {
        clearInterval(intervalTimerRef.current)
        intervalTimerRef.current = null
      }
    }

    if (isElectron && checkIntervalMs && checkIntervalMs > 0) {
      intervalTimerRef.current = setInterval(() => {
        checkForUpdatesRef.current?.()
      }, checkIntervalMs)

      return clearExistingInterval
    } else {
      clearExistingInterval()
      return undefined
    }
  }, [isElectron, checkIntervalMs])

  return {
    availableVersion,
    statusMessage,
    isUpdateAvailable,
    isUpdateDownloaded,
    isChecking,
    error,
    isUpdateBlocked,
    updateBlockedDownloadUrl,
    isUpdateNotAvailable,
    downloadPercent,
    updateRequirement,
    lastInstallOutcome,
    updatePermissionHelpUrl,
    lastCheckStatus,
    permissionRecheckCount,
    checkForUpdates,
    updateIgnoringInstallPermission,
    quitAndInstall,
    setClearMessageDelay,
  }
}
